// Роли и права в интерфейсе.
//
// Источник правды - справочник с сервера (useRolesStore): роли и права
// редактируются в «Настройки → Роли и права». Зашитые ниже значения нужны
// только на первые мгновения после входа, пока справочник не загрузился, и
// повторяют значения по умолчанию из backend/app/constants/roles.py.
//
// Интерфейс лишь прячет то, что сервер всё равно не даст сделать: каждая
// проверка продублирована на сервере.

import { useRolesStore } from '../store/rolesStore'

export const LM = 'operator'
export const SENIOR_LM = 'senior_lm'
export const HEAD_GEO = 'head_geo'
export const SUPER_ADMIN = 'teamlead'
export const DEV_ADMIN = 'superadmin'
export const AM = 'am'

const STATIC_LABELS: Record<string, string> = {
    [LM]: 'ЛМ',
    [SENIOR_LM]: 'Старший ЛМ',
    [HEAD_GEO]: 'Head of GEO',
    [AM]: 'АМ',
    [SUPER_ADMIN]: 'СуперАдминистратор',
    [DEV_ADMIN]: 'Админ разработки',
}

export const ROLE_LABELS = STATIC_LABELS

// Цвета для системных ролей; у заведённых в настройках - нейтральный.
export const ROLE_COLORS: Record<string, string> = {
    [LM]: 'default',
    [SENIOR_LM]: 'blue',
    [HEAD_GEO]: 'cyan',
    [AM]: 'magenta',
    [SUPER_ADMIN]: 'gold',
    [DEV_ADMIN]: 'purple',
}

export const ROLE_AVATAR_COLORS: Record<string, string> = {
    [LM]: '#1890ff',
    [SENIOR_LM]: '#0958d9',
    [HEAD_GEO]: '#13c2c2',
    [AM]: '#c41d7f',
    [SUPER_ADMIN]: '#f56a00',
    [DEV_ADMIN]: '#722ed1',
}

export const ROLE_ORDER = [LM, SENIOR_LM, HEAD_GEO, AM, SUPER_ADMIN, DEV_ADMIN]

export const VIEW_ALL_LEADS = 'view_all_leads'
export const VIEW_ALL_TASKS = 'view_all_tasks'
export const TRANSFER_OWN_LEAD = 'transfer_own_lead'
export const ASSIGN_LEAD = 'assign_lead'
export const TRANSFER_BULK = 'transfer_bulk'
export const EDIT_FOREIGN_LEAD = 'edit_foreign_lead'
export const DELETE_ANYTHING = 'delete_anything'
export const GRANT_ROLES = 'grant_roles'
export const CLOSE_LEAD = 'close_lead'
export const REOPEN_LEAD = 'reopen_lead'
export const MANAGE_SETTINGS = 'manage_settings'

// Запасной вариант до загрузки справочника.
const STATIC_PERMISSIONS: Record<string, string[]> = {
    [LM]: [CLOSE_LEAD, REOPEN_LEAD],
    [SENIOR_LM]: [
        VIEW_ALL_LEADS, VIEW_ALL_TASKS, TRANSFER_OWN_LEAD, ASSIGN_LEAD,
        TRANSFER_BULK, EDIT_FOREIGN_LEAD, CLOSE_LEAD, REOPEN_LEAD,
    ],
    [HEAD_GEO]: [VIEW_ALL_LEADS, VIEW_ALL_TASKS, EDIT_FOREIGN_LEAD],
    [AM]: [],
    [SUPER_ADMIN]: [
        VIEW_ALL_LEADS, VIEW_ALL_TASKS, TRANSFER_OWN_LEAD, ASSIGN_LEAD,
        TRANSFER_BULK, EDIT_FOREIGN_LEAD, GRANT_ROLES, CLOSE_LEAD,
        REOPEN_LEAD, MANAGE_SETTINGS,
    ],
}

const catalogRole = (code?: string | null) =>
    useRolesStore.getState().catalog?.roles.find((role) => role.code === code)

export const roleLabel = (role?: string | null) =>
    catalogRole(role)?.label || STATIC_LABELS[role || ''] || role || '—'

export const can = (role: string | undefined | null, permission: string) => {
    // «Админу разработки» доступно всё - как и на сервере.
    if (role === DEV_ADMIN) return true
    const entry = catalogRole(role)
    if (entry) return entry.permissions.includes(permission)
    return (STATIC_PERMISSIONS[role || ''] || []).includes(permission)
}

export const seesAllLeads = (role?: string | null) => can(role, VIEW_ALL_LEADS)
export const seesOthersTasks = (role?: string | null) => can(role, VIEW_ALL_TASKS)

// Старшинство роли - её порядок в списке. Задачи руководитель видит и ставит
// только тем, кто младше. Как на сервере.
const STATIC_RANK: Record<string, number> = {
    [LM]: 10, [SENIOR_LM]: 20, [HEAD_GEO]: 30, [AM]: 40, [SUPER_ADMIN]: 50,
}
export const roleRank = (role?: string | null) => {
    if (role === DEV_ADMIN) return Number.MAX_SAFE_INTEGER
    const entry = catalogRole(role)
    if (entry) return entry.sort_order
    return STATIC_RANK[role || ''] ?? 0
}

// Кто настраивает роли и доступ к статусам - только «Админ разработки», как на сервере.
export const ROLE_MANAGERS = [DEV_ADMIN]
export const canManageRoles = (role?: string | null) => ROLE_MANAGERS.includes(role || '')
export const managesSettings = (role?: string | null) => can(role, MANAGE_SETTINGS)

// Список ролей для выпадающих списков. Роль «Админ разработки» выдаёт только
// он сам, поэтому остальным она в списке не показывается.
export const roleOptions = (includeDevAdmin: boolean) => {
    const roles = useRolesStore.getState().catalog?.roles
    const options = roles
        ? roles.map((role) => ({ value: role.code, label: role.label }))
        : ROLE_ORDER.map((code) => ({ value: code, label: roleLabel(code) }))
    return options.filter((option) => includeDevAdmin || option.value !== DEV_ADMIN)
}
